// Buyer-facing site preview for the /demo experience — what the prospect
// sees after the agency sends them a link. Mirrors the production
// PreviewSitePage chrome (Preview header + sandboxed iframe + Purchase
// modal) but reads from in-memory demo state and never touches the backend.
// Checkout is simulated with a toast since the demo processes no payment.

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Eye, ShoppingCart, X, Sparkles, CheckCircle, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useDemoState } from './DemoStateProvider';

export function DemoPreviewSitePage() {
  const { token } = useParams<{ token: string }>();
  const { sites } = useDemoState();
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);

  const site = sites.find((s) => s.previewToken === token);

  if (!site) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0b0b0f' }}>
        <div
          className="text-center max-w-md rounded-2xl p-8"
          style={{ background: 'rgba(20,20,30,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <h1 className="text-2xl font-bold text-white mb-4">Preview Not Found</h1>
          <p className="text-gray-400 mb-6">This preview link may have expired or is invalid.</p>
          <Link
            to="/demo/sites"
            className="inline-block px-5 py-2 rounded-lg text-white font-semibold"
            style={{ background: 'linear-gradient(135deg, #B08080, #c49494)' }}
          >
            Back to Sites
          </Link>
        </div>
      </div>
    );
  }

  const { name, templateType, generatedHtml } = site;
  const price = site.price ?? 29900;

  const handlePurchase = () => {
    setPurchaseLoading(true);
    setTimeout(() => {
      setPurchaseLoading(false);
      setShowPurchaseModal(false);
      toast.success('Demo checkout — Stripe would handle real payment here.');
    }, 1100);
  };

  return (
    <div className="min-h-screen" style={{ background: '#0b0b0f' }}>
      {/* Preview header bar */}
      <div
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl"
        style={{ background: 'rgba(11,11,15,0.95)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2" style={{ color: '#B08080' }}>
              <Eye className="w-5 h-5" />
              <span className="font-semibold">Preview</span>
            </div>
            <div>
              <p className="text-sm font-medium text-white">{name}</p>
              <p className="text-xs text-gray-500">Website Preview</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 bg-white/5 px-2 py-1 rounded">{templateType}</span>
            <button
              onClick={() => setShowPurchaseModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
              style={{ background: 'linear-gradient(135deg, #B08080, #c49494)' }}
            >
              <ShoppingCart className="w-4 h-4" />
              Purchase Site
            </button>
          </div>
        </div>
      </div>

      {/* Site preview iframe */}
      <div className="pt-16">
        <iframe
          srcDoc={generatedHtml}
          className="w-full border-0"
          style={{ height: 'calc(100vh - 4rem)' }}
          title="Site Preview"
          sandbox="allow-scripts"
        />
      </div>

      {/* Purchase modal */}
      {showPurchaseModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => e.target === e.currentTarget && setShowPurchaseModal(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl p-6"
            style={{ background: 'rgba(20,20,30,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
                >
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Purchase Website</h2>
                  <p className="text-xs text-gray-400">One-time payment</p>
                </div>
              </div>
              <button
                onClick={() => setShowPurchaseModal(false)}
                className="p-2 rounded-lg hover:bg-white/10 text-gray-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-400">Website</span>
                <span className="text-white font-medium">{name}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-400">Template</span>
                <span className="text-white font-medium">{templateType}</span>
              </div>
              <div className="border-t border-white/10 my-3" />
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Total</span>
                <span className="text-2xl font-bold text-white">${(price / 100).toFixed(0)}</span>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3 text-sm">
                <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#B08080' }} />
                <span className="text-gray-300">Full ownership of all website files</span>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <Download className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#B08080' }} />
                <span className="text-gray-300">Download as ZIP for any hosting provider</span>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#B08080' }} />
                <span className="text-gray-300">Step-by-step deployment guide included</span>
              </div>
            </div>

            <button
              onClick={handlePurchase}
              disabled={purchaseLoading}
              className="w-full py-3 rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-all"
              style={{
                background: purchaseLoading ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #10b981, #059669)',
                cursor: purchaseLoading ? 'wait' : 'pointer',
              }}
            >
              {purchaseLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ShoppingCart className="w-5 h-5" />
                  Proceed to Checkout
                </>
              )}
            </button>

            <p className="text-xs text-center text-gray-500 mt-4">Secure payment powered by Stripe</p>
          </div>
        </div>
      )}
    </div>
  );
}
